import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  Stack,
  TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTextTemplate } from '../../hooks/useTextTemplate';
import FolderItem from './FolderItem';
import AuthContext from '../../context/authContext';

const parseTemplateContent = (content, variables = {}) => {
  if (!content) return '';
  return content.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] || match;
  });
};

const TemplateSelectionModal = ({ open, onClose, onSelectTemplate, selectedEmail }) => {
  const authCtx = useContext(AuthContext);
  const {
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    isLoading,
    error: templateError,
  } = useTextTemplate();
  const [templates, setTemplates] = useState([]);
  const [hoveredTemplate, setHoveredTemplate] = useState(null);
  const [templateFormMode, setTemplateFormMode] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const nameRef = useRef(null);
  const descriptionRef = useRef(null);
  const folderNameRef = useRef(null);
  const tagsRef = useRef(null);
  const contentRef = useRef(null);

  const canCreate = authCtx.checkRight('textTemplate:create');
  const canUpdate = authCtx.checkRight('textTemplate:update');
  const canDelete = authCtx.checkRight('textTemplate:delete');

  const loadTemplates = useCallback(async () => {
    try {
      const response = await fetchTemplates();
      if (response?.success) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Fehler beim Laden der Vorlagen');
    }
  }, [fetchTemplates]);

  useEffect(() => {
    if (open) {
      loadTemplates();
    } else {
      setHoveredTemplate(null);
      setTemplateFormMode(null);
      setEditingTemplate(null);
      setDeleteTarget(null);
    }
  }, [loadTemplates, open]);

  const getTemplateVariables = () => {
    if (!selectedEmail) return {};

    const name = selectedEmail.from.split(' <')[0];
    return { name };
  };

  const handleTemplateSelect = template => {
    if (!template || !template.content) return;

    const variables = getTemplateVariables();
    const parsedContent = parseTemplateContent(template.content, variables);
    onSelectTemplate(parsedContent);
  };

  const handleCreateClick = () => {
    setEditingTemplate(null);
    setTemplateFormMode('create');
  };

  const handleEditClick = template => {
    setEditingTemplate(template);
    setTemplateFormMode('edit');
  };

  const handleCloseTemplateForm = () => {
    setTemplateFormMode(null);
    setEditingTemplate(null);
  };

  const readFormPayload = () => {
    const tags = (tagsRef.current?.value || '')
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);

    return {
      name: nameRef.current?.value.trim() || '',
      description: descriptionRef.current?.value.trim() || '',
      folderName: folderNameRef.current?.value.trim() || 'root',
      tags,
      content: contentRef.current?.value.trim() || '',
    };
  };

  const handleSubmitTemplate = async () => {
    const payload = readFormPayload();
    if (!payload.name) {
      toast.error('Bitte gib einen Namen für die Textvorlage ein');
      return;
    }
    if (!payload.content) {
      toast.error('Bitte gib einen Inhalt für die Textvorlage ein');
      return;
    }

    try {
      const response =
        templateFormMode === 'edit'
          ? await updateTemplate(editingTemplate._id, payload)
          : await createTemplate(payload);

      if (!response?.success) {
        return;
      }

      toast.success(
        templateFormMode === 'edit'
          ? 'Textvorlage wurde aktualisiert'
          : 'Textvorlage wurde erstellt'
      );
      handleCloseTemplateForm();
      await loadTemplates();
      if (templateFormMode === 'edit') {
        setHoveredTemplate(response.data);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Fehler beim Speichern der Textvorlage');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const response = await deleteTemplate(deleteTarget._id);
      if (!response?.success) {
        return;
      }

      toast.success('Textvorlage wurde gelöscht');
      if (hoveredTemplate?._id === deleteTarget._id) {
        setHoveredTemplate(null);
      }
      setDeleteTarget(null);
      await loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Fehler beim Löschen der Textvorlage');
    }
  };

  const formTitle =
    templateFormMode === 'edit' ? 'Textvorlage bearbeiten' : 'Textvorlage erstellen';
  const formKey = editingTemplate?._id || 'new-template';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography variant='h6'>Textvorlage auswählen</Typography>
            {canCreate && (
              <Button variant='contained' size='small' onClick={handleCreateClick}>
                Neue Vorlage
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {templateError && toast.error(templateError)}
          <Box sx={{ display: 'flex', height: '400px' }}>
            <Box
              sx={{
                width: '50%',
                borderRight: 1,
                borderColor: 'divider',
                p: 2,
                overflow: 'auto',
              }}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {templates.map(folder => (
                    <FolderItem
                      key={folder.name}
                      folder={folder}
                      onTemplateSelect={handleTemplateSelect}
                      onTemplateHover={setHoveredTemplate}
                      onTemplateEdit={handleEditClick}
                      onTemplateDelete={setDeleteTarget}
                      canEdit={canUpdate}
                      canDelete={canDelete}
                    />
                  ))}
                  {templates.length === 0 && (
                    <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
                      Keine Textvorlagen vorhanden
                    </Typography>
                  )}
                </List>
              )}
            </Box>
            <Box sx={{ width: '50%', p: 2 }}>
              {hoveredTemplate ? (
                <>
                  <Typography variant='h6' gutterBottom>
                    {hoveredTemplate.name}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' gutterBottom>
                    {hoveredTemplate.description}
                  </Typography>
                  <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.100', color: 'grey.800' }}>
                    <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
                      {parseTemplateContent(hoveredTemplate.content, getTemplateVariables())}
                    </Typography>
                  </Paper>
                </>
              ) : (
                <Typography
                  variant='body1'
                  color='text.secondary'
                  sx={{ textAlign: 'center', mt: 4 }}
                >
                  Bewege die Maus über eine Vorlage, um eine Vorschau zu sehen
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        key={formKey}
        open={Boolean(templateFormMode)}
        onClose={handleCloseTemplateForm}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>{formTitle}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              inputRef={nameRef}
              label='Name'
              defaultValue={editingTemplate?.name || ''}
              required
              fullWidth
              autoFocus
            />
            <TextField
              inputRef={descriptionRef}
              label='Beschreibung'
              defaultValue={editingTemplate?.description || ''}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              inputRef={folderNameRef}
              label='Ordner'
              defaultValue={
                editingTemplate?.folderName === 'root' ? '' : editingTemplate?.folderName || ''
              }
              helperText='Leer lassen für root'
              fullWidth
            />
            <TextField
              inputRef={tagsRef}
              label='Tags'
              defaultValue={(editingTemplate?.tags || []).join(', ')}
              helperText='Mehrere Tags mit Komma trennen'
              fullWidth
            />
            <TextField
              inputRef={contentRef}
              label='Inhalt'
              defaultValue={editingTemplate?.content || ''}
              multiline
              minRows={8}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateForm}>Abbrechen</Button>
          <Button onClick={handleSubmitTemplate} variant='contained' disabled={isLoading}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Textvorlage löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest du die Textvorlage "{deleteTarget?.name}" wirklich löschen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button
            onClick={handleConfirmDelete}
            color='error'
            variant='contained'
            disabled={isLoading}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplateSelectionModal;
